//Built-in libraries

//Third party libraries
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
//Services
import { addApplicationFile, deleteApplicationFile, signApplicationPublicAsset } from '@/api/Rover/ApplicationForm';
import { useFileUpload } from '@/hooks/useFileUpload';
import { notifyUser } from '@/store/Toast';
import { accountingFormatNumber, formatNumber } from '@/utils';
//Custom components
import AttachmentContainer, { Attachment } from '../Attachments';

//STP Libraries

//Assets and Miscellaneous
import type { IGetApplicationServiceResDataFile, ISignApplicationPublicAssetPayload } from '@/api/Rover/ApplicationForm/types';
import { RoverReactQueryKey } from '@/constants/ReactQueryKeys';
import type { SignedApplicationAssetResponse } from '@/types/Shared';

//Component Begin

export interface IProjectDetailsProps {
  projectDetails: any;
  files: IGetApplicationServiceResDataFile[];
}
export default function ProjectDetails({ projectDetails, files }: IProjectDetailsProps) {
  const { applicationFormId, applicationId } = useParams();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { upload } = useFileUpload({
    signAsset: signApplicationPublicAsset,
  });

  const projectDetailsFiles = files.filter((file) => file.field === 'additionalDocs');

  const uploadFile = useMutation((payload: { signAssetPayload: ISignApplicationPublicAssetPayload[]; files: File[] }) => upload(payload), {
    onSuccess: (response: any) => {
      uploadApplicationFiles.mutate(response);
    },
    onError: (_error: any) => {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Failed to upload document',
          toastType: 'failed',
        })
      );
    },
  });

  const uploadApplicationFiles = useMutation(
    (payload: { sign: SignedApplicationAssetResponse; files: File[]; response: any }) => {
      const { files, sign } = payload;
      return addApplicationFile({
        applicationId: String(applicationId),
        files: [
          {
            name: files[0].name,
            path: sign.path,
            key: sign.key,
            type: files[0].type,
            field: 'additionalDocs',
            description: 'Additional Docs',
          },
        ],
      });
    },
    {
      onSuccess: (_, payload: { files: File[] }) => {
        queryClient.invalidateQueries([RoverReactQueryKey.GET_APPLICATION_DETAILS]);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `${payload.files[0].name} is successfully uploaded.`,
            toastType: 'success',
          })
        );
      },
      onError: (_error: any) => {
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Failed to upload document',
            toastType: 'failed',
          })
        );
      },
    }
  );

  const handleUploadDocument = (files: File[]) => {
    const fileExist = projectDetailsFiles?.find((doc) => doc?.name === files[0].name);
    if (fileExist) {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'File already exists.',
          toastType: 'failed',
        })
      );
    } else {
      uploadFile.mutate({
        signAssetPayload: [
          {
            fileName: files[0].name,
            fileType: files[0].type,
            fileSize: files[0].size,
            formId: String(applicationFormId),
          },
        ],
        files,
      });
    }
  };

  const deleteFile = useMutation(
    (payload: { key: string }) =>
      deleteApplicationFile({
        ...payload,
        applicationId: String(applicationId),
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([RoverReactQueryKey.GET_APPLICATION_DETAILS]);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Successfully deleted file',
            toastType: 'success',
          })
        );
      },
    }
  );

  const handleFileDelete = (key: string) => {
    deleteFile.mutate({ key });
  };

  return (
    <section aria-labelledby='applicant-information-title'>
      <div className='bg-white shadow sm:rounded-lg'>
        <div className='py-5'>
          <div className='px-4 py-2 sm:px-6 border-l-4 border-primaryColor-normal'>
            <h2 id='applicant-information-title' className='m-0 text-lg leading-6 font-medium text-primaryColor-normal'>
              Project Details
            </h2>
          </div>
        </div>
        <div className='border-t border-gray-200 px-4 py-5 sm:px-6'>
          <dl className='grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2 mb-8'>
            <div className='sm:col-span-1'>
              <dt className='text-sm font-medium text-gray-500'>Address</dt>
              <dd className='mt-1 text-sm text-gray-900'>
                {projectDetails.streetAddress}, {projectDetails.city}, {projectDetails.state}, {projectDetails.zipCode}
              </dd>
            </div>
            <div className='sm:col-span-1'>
              <dt className='text-sm font-medium text-gray-500'>Deal Type</dt>
              <dd className='mt-1 text-sm text-gray-900'>{projectDetails.dealType}</dd>
            </div>
            <div className='sm:col-span-1'>
              <dt className='text-sm font-medium text-gray-500'>Proposed Number of Units</dt>
              <dd className='mt-1 text-sm text-gray-900'>{formatNumber(projectDetails.proposedUnits)}</dd>
            </div>
            <div className='sm:col-span-1'>
              <dt className='text-sm font-medium text-gray-500'>Is Proposed Project "By Right" ?</dt>
              <dd className='mt-1 text-sm text-gray-900'>{projectDetails.projectByRight}</dd>
            </div>
            <div className='sm:col-span-1'>
              <dt className='text-sm font-medium text-gray-500'>Construction Type</dt>
              <dd className='mt-1 text-sm text-gray-900'>{projectDetails.constructionType}</dd>
            </div>
            <div className='sm:col-span-1'>
              <dt className='text-sm font-medium text-gray-500'>Acquisition Type</dt>
              <dd className='mt-1 text-sm text-gray-900'>{projectDetails.acquisitionType}</dd>
            </div>
            <div className='sm:col-span-1'>
              <dt className='text-sm font-medium text-gray-500'>Status of Site Control</dt>
              <dd className='mt-1 text-sm text-gray-900'>{projectDetails.siteControlStatus}</dd>
            </div>
            <div className='sm:col-span-1'>
              <dt className='text-sm font-medium text-gray-500'>Estimated Acquisition Cost</dt>
              <dd className='mt-1 text-sm text-gray-900'>
                {accountingFormatNumber({
                  num: projectDetails.acquisitionCostEstimate,
                  format: 'en-US',
                  currency: 'USD',
                })}
              </dd>
            </div>
            <div className='sm:col-span-1'>
              <dt className='text-sm font-medium text-gray-500'>Estimated Development Cost</dt>
              <dd className='mt-1 text-sm text-gray-900'>
                {accountingFormatNumber({
                  num: projectDetails.developmentCostEstimate,
                  format: 'en-US',
                  currency: 'USD',
                })}
              </dd>
            </div>
            <div className='sm:col-span-1'>
              <dt className='text-sm font-medium text-gray-500'>Estimated Pre-Development Cost</dt>
              <dd className='mt-1 text-sm text-gray-900'>
                {accountingFormatNumber({
                  num: projectDetails.preDevelopmentCostEstimate,
                  format: 'en-US',
                  currency: 'USD',
                })}
              </dd>
            </div>
            <div className='sm:col-span-1'>
              <dt className='text-sm font-medium text-gray-500'>More Information About Project</dt>
              <dd className='mt-1 text-sm text-gray-900'>{projectDetails.moreInfo}</dd>
            </div>
          </dl>
          <AttachmentContainer
            title='Additional Document'
            uploadConfig={{
              onUpload: handleUploadDocument,
              isUploading: uploadFile.isLoading,
            }}
            isEmpty={!projectDetailsFiles || projectDetailsFiles?.length === 0}
          >
            {projectDetailsFiles?.map((doc) => (
              <Attachment
                title={doc?.name || ''}
                doc={doc}
                key={doc?.url}
                deleteFileHandler={handleFileDelete}
                onClick={() => {
                  window.open(doc?.url || '');
                }}
              />
            ))}
          </AttachmentContainer>
        </div>
      </div>
    </section>
  );
}
